import { gql } from '@apollo/client';
import { ColumnData } from 'ui/tableHeader/constants';
import { TableCell } from 'ui/tableCell/TableCell';
import { Combinator } from 'utils/combinatorTypes';
import { TableHeaderLabel } from './components/AnonymousUserTracking.styled';
import { CriteriaTableColumn } from './types';

export const MAX_LIVE_CRITERIA_COUNT = 10;
export const CRITERIA_NAME_MAX_LENGTH = 100;
export const CRITERIA_DESCRIPTION_MAX_LENGTH = 255;

export const CONTENT = {
  criteriaIndexPage: {
    title: 'Unknown User Criteria',
    newCriteriaBtnLabel: 'New criteria',
    criteriaRetentionTakeover: 'Retention period',
  },
  createCriteriaPage: {
    title: 'New Unknown User Criteria',
    criteriaHeader: 'Criteria',
    detailsHeader: 'Details',
    cancelBtnLabel: 'Cancel',
    createBtnLabel: 'Create criteria',
    breadcrumbLabel: 'All unknown user criteria',
  },
  createCriteriaForm: {
    nameLabel: 'Name',
    descriptionLabel: 'Description',
    autoEnableLabel: 'Status',
  },
  criteriaBuilder: {
    description: 'Add an unknown visitor to your project if they...',
    eventOption: 'Perform event',
    fieldOption: 'Have user field',
  },
  emptyState: {
    defaultEmptyTitle: 'No criteria yet',
    defaultEmptyBody: 'Get started by creating a new criteria or learn more by visiting our ',
    defaultEmptyLinkLabel: 'Knowledge Center',
    filteredEmptyTitle: 'No matching results',
    filteredEmptyBody: 'Try adjusting your filters or searching by different keywords.',
  },
  criteriaDetailsPage: {
    breadcrumbLabel: 'All unknown user criteria',
    segmentationButtonLabel: 'View users in segmentation',
    totalUserCountTooltipContent:
      'Total number of users have been added to your project since this criteria’s creation date',
  },
};

export const AUA_BASE_PAGE_PATH = '/users/unknownUserActivation';
export const AUA_CREATE_PAGE_PATH = `${AUA_BASE_PAGE_PATH}/create`;
export const AUA_DETAILS_PAGE_PATH = (criteriaId?: number | string) =>
  `${AUA_BASE_PAGE_PATH}${criteriaId ? `/${criteriaId}` : ''}`;

export const AUT_DOCUMENTATION_LINK = '/';

export const AnonCriteriaFragment = gql`
  fragment AnonCriteria on AnonUserCriteria {
    id
    isArchived
    updatedByUser {
      fullName
      avatarUrl
    }
    creatorUser {
      fullName
      avatarUrl
    }
    createdAt
    updatedAt
    fields {
      name
      description
      isLive
      searchQuery
    }
  }
`;

export const TOAST_ACTIONS = {
  createCriteria: 'createCriteria',
  archiveCriteria: 'archiveCriteria',
  unarchiveCriteria: 'unarchiveCriteria',
  activateCriteria: 'activateCriteria',
  deactivateCriteria: 'deactivateCriteria',
  editDescription: 'editCriteriaDescription',
  editName: 'editCriteriaName',
};

export const TABLE_COLUMNS: (ColumnData & { RowCell: ({ row }: { row: any }) => JSX.Element })[] = [
  {
    name: <TableHeaderLabel>Unknown user criteria</TableHeaderLabel>,
    value: CriteriaTableColumn.ANONYMOUS_CRITERIA,
    align: 'flex-start',
    sortable: true,
    RowCell: ({ row }) => <TableCell>{row[CriteriaTableColumn.ANONYMOUS_CRITERIA]}</TableCell>,
  },
  {
    name: <TableHeaderLabel>Status</TableHeaderLabel>,
    value: CriteriaTableColumn.STATUS,
    align: 'flex-start',
    sortable: true,
    RowCell: ({ row }) => <TableCell>{row[CriteriaTableColumn.STATUS]}</TableCell>,
    width: 120,
  },
  {
    name: <TableHeaderLabel>Activity</TableHeaderLabel>,
    value: CriteriaTableColumn.ACTIVITY,
    align: 'flex-start',
    sortable: false,
    RowCell: ({ row }) => <TableCell>{row[CriteriaTableColumn.ACTIVITY]}</TableCell>,
    width: 140,
  },
  {
    name: <TableHeaderLabel>Last modified</TableHeaderLabel>,
    value: CriteriaTableColumn.LAST_MODIFIED,
    align: 'flex-start',
    sortable: true,
    RowCell: ({ row }) => <TableCell>{row[CriteriaTableColumn.LAST_MODIFIED]}</TableCell>,
    width: 270,
  },
  {
    name: '',
    value: CriteriaTableColumn.ACTIONS,
    align: 'flex-end',
    sortable: false,
    RowCell: ({ row }) => <TableCell>{row[CriteriaTableColumn.ACTIONS]}</TableCell>,
    width: 80,
  },
];

export const LIVE_STATUS_OPTIONS = [
  {
    id: 'live',
    value: 'true',
    label: 'Live',
    description:
      'Begin adding users to this project that match this criteria immediately. Criteria can be turned off later.',
  },
  {
    id: 'off',
    value: 'false',
    label: 'Off',
    description:
      'Do not begin adding users that match this criteria to this project immediately. Criteria will need to be turned on later.',
  },
];

export const MODAL_CONTENT = {
  ARCHIVE_MODAL_HEADER: 'Archive criteria?',
  UNARCHIVE_MODAL_HEADER: 'Unarchive criteria?',
  ARCHIVE_MODAL_BODY: (criteriaName: string) => (
    <>
      Are you sure that you want to archive <strong>{criteriaName}</strong>?<br />
      <br />
      The criteria will be turned off and will no longer add users to this project. If you change
      your mind, you can unarchive and turn on the criteria later.
    </>
  ),
  UNARCHIVE_MODAL_BODY: (criteriaName: string) => (
    <>
      Are you sure that you want to unarchive <strong>{criteriaName}</strong>?<br />
      <br />
      This criteria will remain off.
    </>
  ),
  ARCHIVE_BUTTON_LABEL: 'Archive criteria',
  UNARCHIVE_BUTTON_LABEL: 'Unarchive criteria',

  TOGGLE_ACTIVE_MODAL_HEADER: 'Turn on criteria?',
  TOGGLE_INACTIVE_MODAL_HEADER: 'Turn off criteria?',
  TOGGLE_ACTIVE_MODAL_BODY: (criteriaName: string) => (
    <>
      Are you sure that you want to turn on <strong>{criteriaName}</strong>?
      <br />
      <br />
      Once this criteria is enabled, new users can be added to your project.
    </>
  ),
  TOGGLE_INACTIVE_MODAL_BODY: (criteriaName: string) => (
    <>
      Are you sure that you want to turn off <strong>{criteriaName}</strong>?
      <br />
      <br />
      This will prevent new users from being added to your project but will not impact users that
      have already been added via this criteria.
    </>
  ),
  TOGGLE_ACTIVE_BUTTON_LABEL: 'Turn on criteria',
  TOGGLE_INACTIVE_BUTTON_LABEL: 'Turn off criteria',

  EDIT_DESCRIPTION_HEADER: 'Edit description',
  EDIT_DESCRIPTION_BUTTON_LABEL: 'Save changes',
};

export const defaultSearchQuery = {
  combinator: Combinator.And,
  searchQueries: [
    {
      combinator: Combinator.And,
      searchQueries: [
        {
          dataType: '',
          searchCombo: {
            combinator: Combinator.And,
            searchQueries: [
              {
                comparatorType: '',
                dataType: '',
                field: '',
                fieldType: '',
                value: '',
              },
            ],
          },
        },
      ],
    },
  ],
};
